import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../utils/api';

const POLL_ONLINE_MS = 30000;
const POLL_OFFLINE_MS = 10000;

/**
 * Tracks whether the backend can actually serve requests.
 *
 * Polls /api/health/db rather than /api/health on purpose: the plain health
 * check is deliberately database-free and answers 200 even when Postgres is
 * unreachable, which is exactly the outage that breaks every useful route.
 */
export default function useApiStatus() {
  const [online, setOnline] = useState(true);
  const [checked, setChecked] = useState(false);
  const timerRef = useRef(null);

  const check = useCallback(async () => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setOnline(false);
      setChecked(true);
      return false;
    }

    let ok = false;
    try {
      const res = await apiFetch('/api/health/db', { timeout: 6000 });
      // A 404 means an older backend without the readiness route. The server
      // still answered, so the API itself is reachable - only treat 5xx and
      // network failures as down.
      ok = res.status < 500;
    } catch (e) {
      ok = false;
    }

    setOnline(ok);
    setChecked(true);
    return ok;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const schedule = (delay) => {
      timerRef.current = setTimeout(async () => {
        if (cancelled) return;
        const ok = await check();
        if (!cancelled) schedule(ok ? POLL_ONLINE_MS : POLL_OFFLINE_MS);
      }, delay);
    };

    // Check immediately, then back off to a slow poll while healthy and a
    // faster one while down so recovery is noticed quickly.
    check().then((ok) => {
      if (!cancelled) schedule(ok ? POLL_ONLINE_MS : POLL_OFFLINE_MS);
    });

    const onFocus = () => check();
    const onOnline = () => check();
    const onOffline = () => setOnline(false);

    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [check]);

  return { online, checked, recheck: check };
}
